// src/components/chat/OpenchatWidget.jsx
import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Minimize2, Maximize2, ExternalLink, Briefcase } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const OpenchatWidget = ({ 
  recipientId, 
  recipientName,
  recipientEmail,
  jobContext,
  isOpen,
  onToggle,
  position = 'bottom-right'
}) => {
  const { userProfile } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

  // Build Openchat embed URL using your existing structure
  const buildOpenchatUrl = () => {
    const baseUrl = 'https://openchat.co.za/embed/chat';
    const params = new URLSearchParams({
      type: 'direct',
      userId: recipientId,
      embedUserName: userProfile.displayName || userProfile.email?.split('@')[0] || 'User',
      embedUserEmail: userProfile.email || '',
      // Add job context as additional parameters
      ...(jobContext && {
        jobId: jobContext.id,
        jobTitle: jobContext.title,
        jobStatus: jobContext.status,
        context: 'parrotoffice'
      })
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  const openchatUrl = buildOpenchatUrl();

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Open in new window
  const openInNewWindow = () => {
    window.open(openchatUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
  };

  if (!isOpen) {
    return (
      <div className={`fixed ${getPositionClasses(position)} z-50`}>
        <button
          onClick={onToggle}
          className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors group"
        >
          <MessageSquare size={24} />
          <span className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Open Chat
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={`fixed ${getPositionClasses(position)} z-50`}>
      <div className={`bg-white rounded-lg shadow-2xl border transition-all duration-300 ${
        isMinimized ? 'w-80 h-14' : 'w-96 h-[500px]'
      }`}>
        {/* Widget Header */}
        <div className="flex items-center justify-between p-3 border-b bg-blue-600 text-white rounded-t-lg">
          <div className="flex items-center space-x-2 min-w-0">
            <MessageSquare size={16} className="flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-medium text-sm block truncate">
                {recipientName || 'Chat'}
              </span>
              {jobContext && (
                <span className="text-blue-100 text-xs block truncate">
                  Job: {jobContext.title}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-1 flex-shrink-0">
            <button
              onClick={openInNewWindow}
              className="p-1 rounded hover:bg-blue-500 transition-colors"
              title="Open in new window"
            >
              <ExternalLink size={14} />
            </button>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 rounded hover:bg-blue-500 transition-colors"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
            </button>
            <button
              onClick={onToggle}
              className="p-1 rounded hover:bg-blue-500 transition-colors"
              title="Close chat"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Job Context Banner */}
        {!isMinimized && jobContext && (
          <div className="bg-blue-50 border-b px-3 py-2">
            <div className="flex items-center text-sm">
              <Briefcase size={14} className="text-blue-600 mr-2 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-blue-900 font-medium block truncate">
                  {jobContext.title}
                </span>
                <span className="text-blue-700 text-xs">
                  Status: {jobContext.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Openchat Iframe */}
        {!isMinimized && (
          <div className="relative" style={{ height: jobContext ? 'calc(100% - 100px)' : 'calc(100% - 56px)' }}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading chat...</p>
                </div>
              </div>
            )}
            
            <iframe
              ref={iframeRef}
              src={openchatUrl}
              className="w-full h-full border-none rounded-b-lg"
              title="Openchat"
              onLoad={handleIframeLoad}
              allow="camera; microphone; clipboard-write"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const getPositionClasses = (position) => {
  switch (position) {
    case 'bottom-right': return 'bottom-6 right-6';
    case 'bottom-left': return 'bottom-6 left-6';
    case 'top-right': return 'top-6 right-6';
    case 'top-left': return 'top-6 left-6';
    default: return 'bottom-6 right-6';
  }
};

export default OpenchatWidget;